import Branch from 'branch-sdk';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import React, { useMemo } from 'react';
import { ForceFullReload } from '../common/components/ForceFullReload';
import { UnsupportedBrowserBanner } from '../common/components/UnsupportedBrowserBanner/UnsupportedBrowserBanner';
import { getGlobals } from '../common/services/Globals';
import { Services, ServicesProvider } from '../common/services/Services';
import { SsrDataPayload } from '../common/services/SsrData';
import { ProxyInfoSlot } from '../common/util/proxyToHost';
import { AppWrappers } from './AppWrappers';

type Props = { ssrData: SsrDataPayload };

export const UnicronApp = ({ Component, ssrData }: AppProps & Props) => {
  const services = useMemo(() => new Services({ ssrData, globals: getGlobals() }), [ssrData]);

  if (services.browser.window) {
    services.browser.window.lastServicesInstance = services;
  }

  if (services.cache.get(ProxyInfoSlot)) {
    /** In the case that a preloader has already proxied this request somewhere else, we want to
     * skip doing any rendering. If the preloading failed and we end up serving this render,
     * trigger a reload so we can try again.
     */
    return <ForceFullReload />;
  }

  return (
    <ServicesProvider value={services}>
      <AppWrappers>
        {services.browser.isOutdated() && <UnsupportedBrowserBanner />}
        <Component />
      </AppWrappers>
    </ServicesProvider>
  );
};

UnicronApp.getInitialProps = async (appContext: AppContext): Promise<AppInitialProps & Props> => {
  const globals = getGlobals();
  const ssrData = { ...globals?.window?.lastServicesInstance?.toData() };
  const pageContext = appContext.ctx;

  const services = new Services({ globals, pageContext, ssrData });

  appContext.ctx.services = services;
  const appProps = await App.getInitialProps(appContext);

  return { ...appProps, ssrData: services.toData() };
};

declare global {
  interface Window {
    lastServicesInstance?: Services;
    branch?: typeof Branch;
  }
}
